import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface MarketplaceRankingRuleRow {
  id: string;
  ruleType: string;
  weight: number;
  displayOrder: number;
  isEnabled: boolean;
  parametersJson: string;
  auditReference: string;
}

export interface MarketplaceRankingProfileRow {
  id: string;
  storeId: string;
  profileKey: string;
  name: string;
  isActive: boolean;
  version: number;
  rules: MarketplaceRankingRuleRow[];
}

export interface MarketplaceRecommendationItemRow {
  offeringId: string;
  title: string;
  slug: string;
  displayPrice: number;
  currencyCode: string;
  reasonCodes: string[];
  sourceSignals: string;
}

export interface MarketplaceRecommendationPreviewRow {
  recommendationType: string;
  isPersonalized: boolean;
  cacheKey: string;
  cacheVersion: string;
  reasonCodes: string[];
  items: MarketplaceRecommendationItemRow[];
}

export interface MarketplaceReviewRow {
  id: string;
  storeId: string;
  offeringId: string;
  customerAccountId: string;
  rating: number;
  title: string;
  body: string;
  moderationStatus: string;
  abuseReportCount: number;
  createdAtUtc: string;
}

export interface PromotedPlacementRow {
  id: string;
  storeId: string;
  offeringId: string;
  placementKey: string;
  disclosureLabel: string;
  boostScore: number;
  isActive: boolean;
  visibleFromUtc?: string | null;
  visibleToUtc?: string | null;
}

export interface FraudRiskAssessmentRow {
  recommendation: string;
  score: number;
  reasonCodes: string[];
  evidenceJson: string;
  policyVersion: string;
}

export interface CreditRiskPreviewRow {
  score: number;
  riskBand: string;
  reasonCodes: string[];
  evidenceJson: string;
  policyVersion: string;
  guardrailNote: string;
}

export interface PortfolioOptimizationPreviewRow {
  concentrationRatio: number;
  delinquencyRatio: number;
  yieldProxy: number;
  recommendation: string;
  reasonCodes: string[];
  evidenceJson: string;
  policyVersion: string;
  guardrailNote: string;
}

@Injectable({ providedIn: 'root' })
export class MarketplaceOptimizationApiService {
  private readonly http = inject(HttpClient);
  private readonly apiBase = environment.apiBaseUrl;

  listRankingProfiles(storeId: string): Observable<MarketplaceRankingProfileRow[]> {
    const params = new HttpParams().set('storeId', storeId);
    return this.http.get<MarketplaceRankingProfileRow[]>(`${this.apiBase}/api/v1/marketplace/discovery/ranking-profiles`, { params });
  }

  upsertRankingProfile(body: unknown): Observable<MarketplaceRankingProfileRow> {
    return this.http.post<MarketplaceRankingProfileRow>(`${this.apiBase}/api/v1/marketplace/discovery/ranking-profiles`, body);
  }

  previewRecommendations(body: unknown): Observable<MarketplaceRecommendationPreviewRow> {
    return this.http.post<MarketplaceRecommendationPreviewRow>(`${this.apiBase}/api/v1/marketplace/discovery/recommendations/preview`, body);
  }

  listReviews(storeId: string, offeringId: string, approvedOnly: boolean): Observable<MarketplaceReviewRow[]> {
    const params = new HttpParams()
      .set('storeId', storeId)
      .set('offeringId', offeringId)
      .set('approvedOnly', String(approvedOnly));
    return this.http.get<MarketplaceReviewRow[]>(`${this.apiBase}/api/v1/marketplace/discovery/reviews`, { params });
  }

  moderateReview(reviewId: string, body: unknown): Observable<MarketplaceReviewRow> {
    return this.http.post<MarketplaceReviewRow>(`${this.apiBase}/api/v1/marketplace/discovery/reviews/${reviewId}/moderate`, body);
  }

  listPromotedPlacements(storeId: string, placementKey: string): Observable<PromotedPlacementRow[]> {
    const params = new HttpParams().set('storeId', storeId).set('placementKey', placementKey);
    return this.http.get<PromotedPlacementRow[]>(`${this.apiBase}/api/v1/marketplace/discovery/promoted-placements`, { params });
  }

  upsertPromotedPlacement(body: unknown): Observable<PromotedPlacementRow> {
    return this.http.post<PromotedPlacementRow>(`${this.apiBase}/api/v1/marketplace/discovery/promoted-placements`, body);
  }

  previewFraudRisk(body: unknown): Observable<FraudRiskAssessmentRow> {
    return this.http.post<FraudRiskAssessmentRow>(`${this.apiBase}/api/v1/payments/risk/fraud/assessments`, body);
  }

  previewCreditRisk(body: unknown): Observable<CreditRiskPreviewRow> {
    return this.http.post<CreditRiskPreviewRow>(`${this.apiBase}/api/v1/payments/risk/credit/preview`, body);
  }

  previewPortfolioOptimization(body: unknown): Observable<PortfolioOptimizationPreviewRow> {
    return this.http.post<PortfolioOptimizationPreviewRow>(`${this.apiBase}/api/v1/payments/risk/portfolio/preview`, body);
  }
}
